import type { LocationSuggestion } from "@/types/city";

export interface LocationData {
    name: string;
    slug: string;
}

/**
 * Save location object to cookie
 * @param location - The full location object to save
 */
export const setLocationCookie = (location: LocationSuggestion | string) => {
    const d = new Date();
    d.setTime(d.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7 days

    if (typeof location === 'string') {
        document.cookie = `location=${encodeURIComponent(location)};expires=${d.toUTCString()};path=/`;
    } else {
        document.cookie = `location=${encodeURIComponent(JSON.stringify(location))};expires=${d.toUTCString()};path=/`;
    }
};

/**
 * Get location object from cookie
 * @returns The location object, or null if not found
 */
export const getLocationCookie = (): LocationSuggestion | null => {
    const match = document.cookie.match(/(?:^|; )location=([^;]*)/);
    if (match && match[1]) {
        try {
            const decoded = decodeURIComponent(match[1]);
            const location = JSON.parse(decoded);
            if (location && (location.name || location.slug)) {
                return location as LocationSuggestion;
            }
        } catch (e) {
           return null; // invalid JSON, fallback to default
        }
    }
    return null;
};

/**
 * Safely parse a raw `location` cookie value read on the server (e.g. via next/headers `cookies()`).
 * Tolerates missing/malformed/legacy-plain-string cookie values instead of throwing.
 * @returns The parsed location data, or null if absent/invalid
 */
export const parseLocationCookie = (raw: string | undefined): LocationData | null => {
    if (!raw) return null;
    try {
        const location = JSON.parse(raw);
        return location ?? null;
    } catch {
        return null; // malformed or legacy plain-string cookie value
    }
};

/**
 * Get simple location string from cookie
 * @returns The location string or empty string if not found
 */
export const getLocationString = (): string => {
    const match = document.cookie.match(/(?:^|; )location=([^;]*)/);  
    if (match && match[1]) {
        const decoded = decodeURIComponent(match[1]);
        try {
            const location = JSON.parse(decoded);
            if (location) {
                const stateName = typeof location.state === 'string' ? location.state : location.state?.name;
                return stateName ? `${location.name}` : `${location.name}`;
                // return stateName ? `${location.name}, ${stateName}` : `${location.name}`;
            }
        } catch (e) {
            return decoded; // old plain string
        }
    }

    return '';
};